<template>
  <KTCodePreview v-bind:title="'Danh sách loại khách hàng'">
    <template v-slot:toolbar>
      <b-row>
        <b-button
          v-show="checkPermission('CUSTOMER_TYPE_CREATE')"
          variant="primary"
          size="sm"
          class="btn btn-primary font-weight-bolder btn-sm"
          @click="openCreateModal"
        >
          <i
            style="font-size: 1rem"
            class="flaticon2-add-1"
          ></i>Tạo mới
        </b-button>
      </b-row>
    </template>

    <template v-slot:preview>
      <b-row>
        <b-col cols="12">
          <b-row>
            <b-col
              cols="6"
              md="4"
              lg="3"
            >
              <b-input-group size="sm">
                <b-form-input
                  v-model="search"
                  placeholder="Tìm kiếm theo tên hoặc mã..."
                  @input="debounceSearch"
                ></b-form-input>
              </b-input-group>
            </b-col>
            <b-col
              cols="6"
              md="4"
              lg="3"
              class="mt-3 mt-md-0"
            >
              <b-form-select
                v-model="statusFilter"
                :options="statusOptions"
                size="sm"
                @change="fetchCustomerTypes"
                class="form-control-sm select-style"
              ></b-form-select>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-table
        :items="customerTypes"
        :fields="fields"
        :busy="isLoading"
        responsive
        hover
        bordered
        show-empty
        empty-text="Không có dữ liệu"
        class="mt-7"
        @row-clicked="handleRowClicked"
        tbody-tr-class="cursor-pointer"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Đang tải...</strong>
          </div>
        </template>

        <template v-slot:cell(name)="row">
          <a
            href="#"
            @click.prevent="openEditModal(row.item)"
            class="text-primary font-weight-bold"
          >
            {{ row.item.name }}
          </a>
        </template>

        <template v-slot:cell(isActive)="row">
          <b-badge :variant="row.item.isActive ? 'success' : 'secondary'">
            {{ row.item.isActive ? 'Hoạt động' : 'Ngưng' }}
          </b-badge>
        </template>

        <template v-slot:cell(createdAt)="row">
          {{ formatDate(row.item.createdAt) }}
        </template>

        <template v-slot:cell(actions)="row">
          <div class="d-flex justify-content-center">
            <b-dropdown
              size="sm"
              no-caret
              right
            >
              <template slot="button-content">
                <i
                  style="font-size: 1rem; padding-right: 0px"
                  class="flaticon2-settings"
                ></i>
              </template>
              <b-dropdown-item @click="openEditModal(row.item)">
                <span style="color: #3f4254; font-size: 12px">
                  <i
                    style="font-size: 1rem"
                    class="flaticon2-pen"
                  ></i>
                  &nbsp; Chỉnh sửa
                </span>
              </b-dropdown-item>
              <b-dropdown-item
                @click="showDeleteAlert(row.item)"
                :disabled="isDeleting"
              >
                <span style="color: #3f4254; font-size: 12px">
                  <b-spinner
                    small
                    v-if="isDeleting && selectedForDelete?.id === row.item.id"
                    class="mr-1"
                  ></b-spinner>
                  <i
                    v-else
                    style="font-size: 1rem; color: #d33"
                    class="flaticon2-rubbish-bin-delete-button"
                  ></i>
                  &nbsp; Xóa
                </span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>

      <b-col>
        <BPaginationNavCustom
          :number-of-pages="totalPages || 1"
          :current-page="currentPage"
          :total-page="totalPages"
          :linkParams="{}"
          @page-change="fetchCustomerTypes"
        />
      </b-col>

      <customer-type-form-modal
        :show="showFormModal"
        :customer-type="selectedCustomerType"
        :is-edit="isEdit"
        @close="closeFormModal"
        @customer-type-saved="handleSaved"
      />
    </template>
  </KTCodePreview>
</template>

<script>
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import localData from '@/utils/saveDataToLocal';
import KTCodePreview from '@/view/content/CodePreview.vue';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import CustomerTypeFormModal from './CustomerTypeFormModal.vue';
import { CustomerTypeService } from './service/customer-type.service';
import { makeToastSuccess } from '@/utils/common';

export default {
  name: 'CustomerTypeList',
  components: {
    KTCodePreview,
    BPaginationNavCustom,
    CustomerTypeFormModal,
  },
  data() {
    return {
      fields: [
        { key: 'name', label: 'Tên loại khách hàng' },
        { key: 'code', label: 'Mã loại' },
        { key: 'isActive', label: 'Trạng thái', class: 'text-center' },
        { key: 'createdAt', label: 'Ngày tạo' },
        { key: 'actions', label: '', class: 'text-center' },
      ],
      customerTypes: [],
      totalItems: 0,
      totalPages: 0,
      currentPage: 1,
      limit: 20,
      search: '',
      statusFilter: null,
      statusOptions: [
        { text: 'Tất cả trạng thái', value: null },
        { text: 'Hoạt động', value: true },
        { text: 'Ngưng hoạt động', value: false },
      ],
      isLoading: false,
      showFormModal: false,
      isEdit: false,
      selectedCustomerType: null,
      isDeleting: false,
      selectedForDelete: null,
      searchTimeout: null,
    };
  },
  created() {
    this.fetchCustomerTypes();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: 'Danh sách loại khách hàng' }]);
  },
  methods: {
    checkPermission(condition) {
      return localData.checkPermission(condition);
    },

    fetchCustomerTypes(page = this.currentPage) {
      this.currentPage = page;
      this.isLoading = true;

      const params = { page: this.currentPage, limit: this.limit };

      if (this.search) params.search = this.search;
      if (this.statusFilter !== null) params.isActive = this.statusFilter;

      CustomerTypeService.getAll(params)
        .then(response => {
          const data = response.data.data;
          this.customerTypes = data.dataset || [];
          this.totalItems = data.totalRow || 0;
          this.totalPages = data.totalPage || 0;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchCustomerTypes();
      }, 500);
    },

    openCreateModal() {
      this.isEdit = false;
      this.selectedCustomerType = { name: '', code: '', isActive: true };
      this.showFormModal = true;
    },

    openEditModal(customerType) {
      this.isEdit = true;
      this.selectedCustomerType = { ...customerType };
      this.showFormModal = true;
    },

    closeFormModal() {
      this.showFormModal = false;
      this.selectedCustomerType = null;
    },

    handleSaved() {
      this.closeFormModal();
      this.fetchCustomerTypes();
    },

    handleRowClicked(row, column, event) {
      event.preventDefault();
      this.openEditModal(row);
    },

    showDeleteAlert(customerType) {
      this.$swal
        .fire({
          title: 'Xóa loại khách hàng!',
          text: `Bạn có chắc muốn xóa loại khách hàng "${customerType.name}" này không?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: 'rgb(221, 51, 51)',
          cancelButtonColor: '#E5EAEE',
          confirmButtonText: 'Xóa',
          cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
          reverseButtons: true,
          showClass: { popup: 'animate__animated animate__fadeInDown' },
          hideClass: { popup: 'animate__animated animate__fadeOutUp' },
        })
        .then(result => {
          if (result.isConfirmed) {
            this.deleteCustomerType(customerType);
          }
        });
    },

    deleteCustomerType(customerType) {
      this.isDeleting = true;
      this.selectedForDelete = customerType;

      CustomerTypeService.delete(customerType.id)
        .then((response) => {
          if(response.data.status === 200){
            makeToastSuccess('Xóa loại khách hàng thành công!');
            this.fetchCustomerTypes();
          }
        })
        .finally(() => {
          this.isDeleting = false;
          this.selectedForDelete = null;
        });
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('vi-VN');
    },
  },
};
</script>
